import { Controller } from "@hotwired/stimulus"
import { showToast } from "../lib/toast"

// AI local-market cost benchmark for the GC (builder) SOV grid.
// The "AI Cost Checker" column (`.js-benchmark-col`, baked into the
// sov-editor GC template) is always visible; "Benchmark costs" puts a
// pulsing "checking…" placeholder on every priced row, fires the rows to
// finances#benchmark_sov in parallel batches (anonymized Gemini calls —
// see the ai-finance-block policy), and fills each row's verdict pill
// (▼ below / ≈ on par / ▲ above the typical local range) as its batch
// returns, plus a summary strip above the grid when all are done.
// Results are AI estimates and are labeled as such everywhere. Cells
// empty if the grid re-renders — re-run after price edits.
export default class extends Controller {
  static targets = ["button", "summary"]
  static values = { url: String }

  BATCH_SIZE = 15

  connect() {
    this._csrf = document.querySelector('meta[name="csrf-token"]')?.content
    this._running = false
  }

  async run() {
    if (this._running) return
    const priced = Array.from(this.element.querySelectorAll("tr[data-line-id]")).filter((row) => {
      const price = parseFloat(row.querySelector("input[data-field='unit_price']")?.value)
      return price > 0 && row.querySelector("[data-benchmark-cell]")
    })
    if (!priced.length) {
      showToast("No line items with unit prices to benchmark yet.", "alert")
      return
    }

    this._running = true
    this._setLoading(true)
    if (this.hasSummaryTarget) this.summaryTarget.innerHTML = ""
    priced.forEach((row) => this._setPlaceholder(row))

    const ids = priced.map((row) => row.dataset.lineId)
    const batches = []
    for (let i = 0; i < ids.length; i += this.BATCH_SIZE) batches.push(ids.slice(i, i + this.BATCH_SIZE))

    const counts = { below: 0, on_par: 0, above: 0, unknown: 0 }
    let location = null
    let failures = 0

    await Promise.all(batches.map(async (batch, i) => {
      try {
        const resp = await fetch(this.urlValue, {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-CSRF-Token": this._csrf, Accept: "application/json" },
          // track_total rides on the first batch only so Mixpanel sees ONE
          // "SOV Benchmarked" event per run, not one per batch.
          body: JSON.stringify({ line_item_ids: batch, ...(i === 0 ? { track_total: ids.length } : {}) })
        })
        const data = await resp.json().catch(() => ({}))
        if (!resp.ok || data.error) throw new Error(data.error || "failed")
        location = location || data.location
        Object.entries(data.results || {}).forEach(([lineId, r]) => {
          counts[r.verdict] = (counts[r.verdict] || 0) + 1
          this._fillCell(lineId, r)
        })
      } catch {
        failures++
      } finally {
        this._clearPending(batch)
      }
    }))

    const judged = Object.values(counts).reduce((a, b) => a + b, 0)
    if (!judged && failures) {
      showToast("Benchmark failed — please try again.", "alert")
    } else {
      if (failures) showToast("Some lines couldn't be checked — run again to retry them.", "alert")
      this._renderSummary(counts, location || "")
    }
    this._running = false
    this._setLoading(false)
  }

  dismissSummary() {
    if (this.hasSummaryTarget) this.summaryTarget.innerHTML = ""
    this.element.querySelectorAll("[data-benchmark-cell]").forEach((cell) => {
      cell.innerHTML = ""
      delete cell.dataset.pending
    })
  }

  // ── Rendering ────────────────────────────────────────────────────────────

  _cell(lineId) {
    return this.element.querySelector(`tr[data-line-id="${lineId}"] [data-benchmark-cell]`)
  }

  _setPlaceholder(row) {
    const cell = row.querySelector("[data-benchmark-cell]")
    if (!cell) return
    cell.dataset.pending = "1"
    cell.innerHTML = `<span class="animate-pulse" style="display:inline-flex;align-items:center;padding:1px 8px;` +
      `border-radius:9999px;font-size:13px;white-space:nowrap;background:#e0e7ff;color:#4f46e5;">checking…</span>`
  }

  // Any cell still marked pending after its batch settles (no result, or
  // the batch failed) goes back to empty.
  _clearPending(batch) {
    batch.forEach((lineId) => {
      const cell = this._cell(lineId)
      if (cell?.dataset.pending) {
        cell.innerHTML = ""
        delete cell.dataset.pending
      }
    })
  }

  _fillCell(lineId, r) {
    const cell = this._cell(lineId)
    if (!cell) return
    delete cell.dataset.pending
    const style = this._pillStyle(r.verdict)
    cell.innerHTML = `<span title="" style="display:inline-flex;align-items:center;gap:3px;` +
      `padding:1px 8px;border-radius:9999px;font-size:13px;white-space:nowrap;cursor:help;${style.css}">` +
      `${style.glyph} ${style.label}</span>`
    cell.firstChild.title = this._tooltip(r)
  }

  _pillStyle(verdict) {
    if (verdict === "above") return { glyph: "▲", label: "above", css: "background:#fee2e2;color:#b91c1c;" }
    if (verdict === "below") return { glyph: "▼", label: "below", css: "background:#dcfce7;color:#15803d;" }
    if (verdict === "on_par") return { glyph: "≈", label: "on par", css: "background:#e5e7eb;color:#4b5563;" }
    return { glyph: "–", label: "n/a", css: "background:#f3f4f6;color:#9ca3af;" }
  }

  _tooltip(r) {
    const parts = []
    if (r.typical_low != null && r.typical_high != null) {
      parts.push(`Typical local range: $${this._fmt(r.typical_low)}–$${this._fmt(r.typical_high)}`)
    }
    if (r.note) parts.push(r.note)
    parts.push("AI estimate — not verified market data.")
    return parts.join("\n")
  }

  _renderSummary(counts, location) {
    if (!this.hasSummaryTarget) return
    const chip = (n, label, cls) => n
      ? `<span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[13px] font-medium ${cls}">${n} ${label}</span>`
      : ""
    this.summaryTarget.innerHTML = `
      <div class="flex items-center flex-wrap gap-2 border border-gray-200 bg-white rounded-lg px-3 py-2 mt-2 text-[13px] text-gray-600">
        <span class="font-medium text-gray-700">AI Cost Checker — ${this._esc(location)}</span>
        ${chip(counts.above, "above local range", "bg-red-100 text-red-700")}
        ${chip(counts.on_par, "on par", "bg-gray-200 text-gray-600")}
        ${chip(counts.below, "below local range", "bg-green-100 text-green-700")}
        ${counts.unknown ? `<span class="text-gray-400">${counts.unknown} couldn’t be judged</span>` : ""}
        <span class="text-gray-400">AI estimates from general market knowledge — not verified cost data. Hover a pill for details.</span>
        <button type="button" data-action="click->sov-benchmark#dismissSummary"
                class="ml-auto text-gray-400 hover:text-gray-600 cursor-pointer" title="Clear results">✕</button>
      </div>`
  }

  _setLoading(loading) {
    if (!this.hasButtonTarget) return
    const btn = this.buttonTarget
    if (loading) {
      btn.disabled = true
      btn.dataset.benchmarkLabel = btn.innerHTML
      btn.innerHTML = `<svg class="animate-spin w-3.5 h-3.5" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" opacity="0.25"></circle>
          <path d="M4 12a8 8 0 018-8" stroke="currentColor" stroke-width="4" stroke-linecap="round"></path>
        </svg> Checking…`
    } else {
      btn.disabled = false
      if (btn.dataset.benchmarkLabel) {
        btn.innerHTML = btn.dataset.benchmarkLabel
        delete btn.dataset.benchmarkLabel
      }
    }
  }

  _fmt(n) {
    return Number(n).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  _esc(s) {
    const div = document.createElement("div")
    div.textContent = s || ""
    return div.innerHTML
  }
}
